import { ScoreboardRankingEntity } from "@/modules/scores/scoreboard/entities/scoreboardRankingEntity"
import { SessionScoreEntity } from "@/modules/scores/scoreboard/entities/sessionScoreEntity"
import { useServicesContext } from "@modules/core/services/services.context"
import { ScoreDifficultyFilterEnum, type ExerciseScoreCategory } from "@modules/scores/scoreTypes"
import { useInfiniteQuery, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const useScoreboard = (
  programId: string,
  sessionId: string,
  exerciseId: string,
  difficulty: ScoreDifficultyFilterEnum,
  category: ExerciseScoreCategory,
) => {
  const { scoreboardApi } = useServicesContext()
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["scoreboard", { programId, sessionId, exerciseId, difficulty, category }],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      const result = await scoreboardApi.getExerciseScoreboard(
        programId,
        sessionId,
        exerciseId,
        difficulty,
        category,
        pageParam,
      )
      return {
        ...result,
        content: result.content.map((ranking) => ScoreboardRankingEntity.fromDto(ranking)),
      }
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0 || lastPage.last) {
        return null
      }
      return lastPage.pageable.pageNumber + 1
    },
  })

  const scoreboardList = useMemo(() => {
    return data?.pages.flatMap((page) => page.content) ?? []
  }, [data])

  return {
    data: { ...data, content: scoreboardList },
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export const useMyRanking = (
  programId: string,
  sessionId: string,
  exerciseId: string | undefined,
  difficulty: ScoreDifficultyFilterEnum,
  category: ExerciseScoreCategory,
) => {
  const { scoreboardApi } = useServicesContext()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["myRanking", programId, sessionId, exerciseId, difficulty, category],
    queryFn: async () => {
      const result = await scoreboardApi.getMyExerciseScoreboardRanking(
        programId,
        sessionId,
        exerciseId!, // typescript thinks exerciseId can be undefined, but it can't be because of the enabled condition
        difficulty,
        category,
      )
      return result ? ScoreboardRankingEntity.fromDto(result) : null
    },
    enabled: !!exerciseId && !!difficulty,
  })

  return { data, isLoading, error, refetch }
}

export const useAthleteScoreboard = (sessionId: string, athleteId: string) => {
  const { scoreboardApi } = useServicesContext()

  const { data, isLoading, error } = useSuspenseQuery({
    queryKey: ["athleteScoreboard", sessionId, athleteId],
    queryFn: async () => {
      const result = await scoreboardApi.getAthleteScoreboard(sessionId, athleteId)
      return result ? SessionScoreEntity.fromDto(result) : null
    },
  })

  return { data, isLoading, error }
}
